/**
 * PlayerFenders.ts — per-position pendulum fenders for the player boat.
 *
 * Each fender is a single particle hanging on a fixed-length massless line
 * from a hanger point rigidly attached to the boat. The particle integrates
 * gravity in world coords; an inextensible distance constraint to the
 * hanger world position is enforced each step (projection method). A simple
 * linear drag plus tangent damping settles the swing.
 *
 * The renderer reads {@link PlayerFenders.getWorldPose} each frame and
 * places the FenderBody mesh so its local `Attach` point coincides with
 * the simulated world Attach position. The rope direction and body
 * orientation are decoupled: `Attach` is a free joint, so the body hangs
 * gravity-upright under that point instead of becoming a rigid extension
 * of the rope.
 *
 * Body frame: +X forward (bow), +Y up, +Z starboard.
 *
 * Out of scope for this module (TODO slice 2):
 *   - Collision with docks / piers (soft contact + reaction force on boat).
 *   - Visible rope between hanger and Attach (RopeRenderer).
 *
 * Hull collision uses a baked half-beam profile sampled from the actual
 * hull mesh vertices (see {@link buildTopsidesProfileFromScene}): for
 * each body-X station the profile stores the maximum |Z| of any vertex
 * in a relevant Y band (a bit below waterline up to slightly above the
 * rail). At runtime the fender Attach point is pushed outward in body-Z
 * so its inboard surface (`|Z| - bodyRadius`) lies just outside that
 * station's half-beam.
 */

import type { Boat } from "./Boat";

type Vec3 = [number, number, number];

/** Constants — tuned for ~0.4 m fender on a 0.45 m line. */
const GRAVITY = 9.81;
/** Linear drag coefficient (1/s). Damps overall velocity. */
const LINEAR_DRAG = 0.4;
/** Extra tangent damping per second. Settles the pendulum quickly. */
const TANGENT_DAMPING = 1.8;
/** PBD-style constraint iterations per step. */
const CONSTRAINT_ITERS = 2;

/**
 * One hanger anchor on the boat: a body-frame position (m) and a logical
 * key that lines up with the {@link CREW_FENDERS} entries so per-fender
 * deploy state can be wired through AppState.
 */
export interface FenderHangerSpec {
  /** CREW_FENDERS key, e.g. "bow_port", "stern_starboard". */
  key: string;
  /** Position of the hanger Empty in boat body frame (m). */
  bodyPos: Vec3;
}

interface FenderInstance {
  spec: FenderHangerSpec;
  /** World position of the fender's Attach point (top of the body). */
  attachWorld: Vec3;
  /** World velocity of the Attach point. */
  attachVel: Vec3;
  /** Latest world rotation matrix for rendering (column-major 3×3). */
  rWorld: Float32Array;
  /** Whether this fender is hanging (true) or stowed at the hanger (false). */
  deployed: boolean;
}

/**
 * Mesh-derived half-beam profile of the hull along body-X. Sampled once
 * at boat-load time and read by {@link PlayerFenders.applyHullCollision}
 * to clamp the Attach point against the real topsides shape.
 */
export interface TopsidesProfile {
  /** Lower body-X bound covered by the profile (m). */
  xMin: number;
  /** Upper body-X bound (m). */
  xMax: number;
  /** Number of stations. */
  n: number;
  /** `halfBeams[i]` = max |Z| at body-X station `i` (m). */
  halfBeams: Float32Array;
}

/** Result returned by {@link PlayerFenders.getWorldPose}. */
export interface FenderWorldPose {
  tx: number;
  ty: number;
  tz: number;
  r: readonly [
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
  ];
}

export class PlayerFenders {
  private readonly fenders: FenderInstance[] = [];
  /** Distance (m) from hanger to fender Attach when at rest. */
  readonly ropeLength: number;
  /**
   * Local-frame offset from FenderBody pivot to the Attach point, in the
   * fender mesh's own body-frame coords after the loader's axis fix.
   * Magnitude defines the visible body height; direction defines which
   * way the body "hangs" relative to the rope.
   */
  readonly localAttachOffset: Vec3;
  private readonly localAttachUnit: Vec3;
  private readonly localAttachMag: number;
  /**
   * Horizontal radius of the fender body (m). Combined with the per-
   * station topsides half-beam to define the body-frame wall the Attach
   * point must stay outside of.
   */
  readonly bodyRadius: number;
  /** Optional mesh-derived half-beam profile; if absent no hull clamp runs. */
  private readonly topsides: TopsidesProfile | null;

  constructor(
    private readonly boat: Boat,
    hangers: readonly FenderHangerSpec[],
    localAttachOffset: Vec3,
    ropeLength = 0.45,
    opts: { bodyRadius?: number; topsides?: TopsidesProfile | null } = {},
  ) {
    this.ropeLength = ropeLength;
    this.bodyRadius = opts.bodyRadius ?? 0.1;
    this.topsides = opts.topsides ?? null;
    this.localAttachOffset = [
      localAttachOffset[0],
      localAttachOffset[1],
      localAttachOffset[2],
    ];
    const mag = Math.hypot(
      localAttachOffset[0],
      localAttachOffset[1],
      localAttachOffset[2],
    );
    this.localAttachMag = mag > 1e-6 ? mag : 1;
    this.localAttachUnit = [
      localAttachOffset[0] / this.localAttachMag,
      localAttachOffset[1] / this.localAttachMag,
      localAttachOffset[2] / this.localAttachMag,
    ];
    for (const h of hangers) {
      const hangerW = this.hangerWorldPos(h.bodyPos);
      this.fenders.push({
        spec: h,
        attachWorld: [hangerW[0], hangerW[1] - ropeLength, hangerW[2]],
        attachVel: [0, 0, 0],
        rWorld: new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]),
        deployed: false,
      });
    }
  }

  get count(): number {
    return this.fenders.length;
  }

  fenderKey(idx: number): string {
    return this.fenders[idx]!.spec.key;
  }

  /** Push per-position deploy flags from AppState. */
  setDeployed(map: Readonly<Record<string, boolean>>): void {
    for (const f of this.fenders) {
      f.deployed = !!map[f.spec.key];
    }
  }

  step(dt: number): void {
    if (dt <= 0) return;
    for (const f of this.fenders) {
      const hangerW = this.hangerWorldPos(f.spec.bodyPos);
      if (!f.deployed) {
        // Stowed: snap to hanger (no dangling body). Reset velocity so it
        // doesn't pop on next deploy.
        f.attachWorld[0] = hangerW[0];
        f.attachWorld[1] = hangerW[1] - this.ropeLength;
        f.attachWorld[2] = hangerW[2];
        f.attachVel[0] = 0;
        f.attachVel[1] = 0;
        f.attachVel[2] = 0;
        this.updateRotation(f, hangerW);
        continue;
      }

      // Semi-implicit Euler.
      f.attachVel[1] -= GRAVITY * dt;
      const dragMul = Math.max(0, 1 - LINEAR_DRAG * dt);
      f.attachVel[0] *= dragMul;
      f.attachVel[1] *= dragMul;
      f.attachVel[2] *= dragMul;
      f.attachWorld[0] += f.attachVel[0] * dt;
      f.attachWorld[1] += f.attachVel[1] * dt;
      f.attachWorld[2] += f.attachVel[2] * dt;

      this.enforceRopeLength(f, hangerW);

      // Hull collision against the mesh-derived topsides profile. Pushes
      // the Attach point outward in body-Z until `|Z| - bodyRadius >=
      // halfBeamAt(bodyX)`, so the fender body lightly rests on the
      // actual topsides shape (narrow at bow, wide amidships, etc.).
      this.applyHullCollision(f);

      // Tangent damping: simple exponential decay on the post-constraint
      // velocity. Acts as air drag on the swinging body.
      const tDamp = Math.max(0, 1 - TANGENT_DAMPING * dt);
      f.attachVel[0] *= tDamp;
      f.attachVel[1] *= tDamp;
      f.attachVel[2] *= tDamp;

      this.updateRotation(f, hangerW);
    }
  }

  /**
   * Push the Attach point outward in body-Z so the fender body just
   * clears the hull's topsides at the current body-X station. The wall
   * position comes from {@link TopsidesProfile.halfBeams} sampled from
   * the real hull mesh, so on a flared hull the fender sits slightly
   * outboard of the rail and on a tumblehome it hangs nearer the rail.
   */
  private applyHullCollision(f: FenderInstance): void {
    const prof = this.topsides;
    if (!prof) return;
    const b = this.boat;
    const side = f.spec.bodyPos[2] >= 0 ? 1 : -1;

    // Body axes (cols of R world←body) — match BoatRenderPass.encode().
    const ch = Math.cos(b.heading);
    const sh = Math.sin(b.heading);
    const cp = Math.cos(b.pitch);
    const sp = Math.sin(b.pitch);
    const cr = Math.cos(b.roll);
    const sr = Math.sin(b.roll);
    const bxX = cp * sh;
    const bxY = sp;
    const bxZ = -cp * ch;
    const byX = -sp * cr * sh + sr * ch;
    const byY = cp * cr;
    const byZ = sp * cr * ch + sr * sh;
    const bzX = sp * sr * sh + cr * ch;
    const bzY = -cp * sr;
    const bzZ = -sp * sr * ch + cr * sh;

    // World offset Attach − boat origin → body frame.
    const dx = f.attachWorld[0] - b.posX;
    const dy = f.attachWorld[1] - b.posY;
    const dz = f.attachWorld[2] - b.posZ;
    const bxLocal = bxX * dx + bxY * dy + bxZ * dz;
    const bzLocal = bzX * dx + bzY * dy + bzZ * dz;

    const halfBeam = sampleHalfBeam(prof, bxLocal);
    if (halfBeam <= 0) return;
    const minDist = halfBeam + this.bodyRadius;

    const insideZone = side > 0 ? bzLocal < minDist : bzLocal > -minDist;
    if (!insideZone) return;
    const hanger = f.spec.bodyPos;
    const localDx = bxLocal - hanger[0];
    const desiredDz = side * minDist - hanger[2];
    const maxDz = Math.sqrt(
      Math.max(0, this.ropeLength * this.ropeLength - localDx * localDx),
    );
    const localDz =
      Math.abs(desiredDz) <= maxDz ? desiredDz : side * maxDz;
    const localDrop = Math.sqrt(
      Math.max(
        0,
        this.ropeLength * this.ropeLength - localDx * localDx - localDz * localDz,
      ),
    );
    const targetX = bxLocal;
    const targetY = hanger[1] - localDrop;
    const targetZ = hanger[2] + localDz;

    f.attachWorld[0] =
      b.posX + bxX * targetX + byX * targetY + bzX * targetZ;
    f.attachWorld[1] =
      b.posY + bxY * targetX + byY * targetY + bzY * targetZ;
    f.attachWorld[2] =
      b.posZ + bxZ * targetX + byZ * targetY + bzZ * targetZ;

    // Strip inward body-Z velocity component (inward = opposite of side).
    const vBz =
      bzX * f.attachVel[0] + bzY * f.attachVel[1] + bzZ * f.attachVel[2];
    const wrongSign = side > 0 ? vBz < 0 : vBz > 0;
    if (wrongSign) {
      f.attachVel[0] -= vBz * bzX;
      f.attachVel[1] -= vBz * bzY;
      f.attachVel[2] -= vBz * bzZ;
    }
  }

  /**
  * Return the model matrix for fender `idx`: world translation + 3x3
  * column-major rotation, such that the FenderBody's local Attach point
  * coincides with the simulated world Attach position while the body hangs
  * gravity-upright from that free joint.
   */
  getWorldPose(idx: number): FenderWorldPose {
    const f = this.fenders[idx]!;
    const r = f.rWorld;
    // T = attachWorld - R * localAttachOffset (column-major application).
    const lx = this.localAttachOffset[0];
    const ly = this.localAttachOffset[1];
    const lz = this.localAttachOffset[2];
    const rOx = r[0]! * lx + r[3]! * ly + r[6]! * lz;
    const rOy = r[1]! * lx + r[4]! * ly + r[7]! * lz;
    const rOz = r[2]! * lx + r[5]! * ly + r[8]! * lz;
    return {
      tx: f.attachWorld[0] - rOx,
      ty: f.attachWorld[1] - rOy,
      tz: f.attachWorld[2] - rOz,
      r: [r[0]!, r[1]!, r[2]!, r[3]!, r[4]!, r[5]!, r[6]!, r[7]!, r[8]!],
    };
  }

  isDeployed(idx: number): boolean {
    return this.fenders[idx]!.deployed;
  }

  /**
   * World-space endpoints of the visible rope between hanger and Attach.
   * `hanger` is the authored GLB/preset point on the rail; `attach` is the
   * simulated top of the fender body. Used by the rope renderer.
   */
  getRopeEndpoints(idx: number): { hanger: Vec3; attach: Vec3 } {
    const f = this.fenders[idx]!;
    const hangerW = this.hangerWorldPos(f.spec.bodyPos);
    return {
      hanger: hangerW,
      attach: [f.attachWorld[0], f.attachWorld[1], f.attachWorld[2]],
    };
  }

  private enforceRopeLength(f: FenderInstance, hangerW: Vec3): void {
    for (let it = 0; it < CONSTRAINT_ITERS; it++) {
      const dx = f.attachWorld[0] - hangerW[0];
      const dy = f.attachWorld[1] - hangerW[1];
      const dz = f.attachWorld[2] - hangerW[2];
      const d = Math.hypot(dx, dy, dz);
      if (d <= 1e-6 || Math.abs(d - this.ropeLength) <= 1e-5) continue;
      const k = this.ropeLength / d;
      f.attachWorld[0] = hangerW[0] + dx * k;
      f.attachWorld[1] = hangerW[1] + dy * k;
      f.attachWorld[2] = hangerW[2] + dz * k;
      const rx = dx / d;
      const ry = dy / d;
      const rz = dz / d;
      const vr =
        f.attachVel[0] * rx + f.attachVel[1] * ry + f.attachVel[2] * rz;
      f.attachVel[0] -= vr * rx;
      f.attachVel[1] -= vr * ry;
      f.attachVel[2] -= vr * rz;
    }
  }

  /**
   * Convert a body-frame point (relative to boat origin) to world coords,
   * honouring full yaw·pitch·roll. Matches the convention used by
   * {@link BoatRenderPass} so hangers track the rendered hull exactly.
   */
  private hangerWorldPos(bodyPos: Vec3): Vec3 {
    const b = this.boat;
    const ch = Math.cos(b.heading);
    const sh = Math.sin(b.heading);
    const cp = Math.cos(b.pitch);
    const sp = Math.sin(b.pitch);
    const cr = Math.cos(b.roll);
    const sr = Math.sin(b.roll);
    // Body axes in world (cols of R), same as BoatRenderPass.encode().
    const bxX = cp * sh;
    const bxY = sp;
    const bxZ = -cp * ch;
    const byX = -sp * cr * sh + sr * ch;
    const byY = cp * cr;
    const byZ = sp * cr * ch + sr * sh;
    const bzX = sp * sr * sh + cr * ch;
    const bzY = -cp * sr;
    const bzZ = -sp * sr * ch + cr * sh;
    const px = bodyPos[0];
    const py = bodyPos[1];
    const pz = bodyPos[2];
    return [
      b.posX + bxX * px + byX * py + bzX * pz,
      b.posY + bxY * px + byY * py + bzY * pz,
      b.posZ + bxZ * px + byZ * py + bzZ * pz,
    ];
  }

  /**
   * Build the world rotation matrix for the fender body. The rope is attached
   * to a free eye at `Attach`, so an angled rope must not tilt the fender
   * itself; gravity keeps the body's local Attach direction pointing up.
   */
  private updateRotation(f: FenderInstance, _hangerW: Vec3): void {
    const ux = this.localAttachUnit[0];
    const uy = this.localAttachUnit[1];
    const uz = this.localAttachUnit[2];
    const vx = 0;
    const vy = 1;
    const vz = 0;
    const dot = ux * vx + uy * vy + uz * vz;
    if (dot > 0.9999) {
      f.rWorld.set([1, 0, 0, 0, 1, 0, 0, 0, 1]);
      return;
    }
    if (dot < -0.9999) {
      // 180° rotation: pick an axis orthogonal to u. Use X if u isn't ±X.
      const ax = Math.abs(ux) < 0.9 ? 1 : 0;
      const ay = Math.abs(ux) < 0.9 ? 0 : 1;
      // Rodrigues with sin=0, cos=-1 ⇒ R = -I + 2·k·kᵀ.
      const kx = ax;
      const ky = ay;
      const kz = 0;
      f.rWorld[0] = -1 + 2 * kx * kx;
      f.rWorld[1] = 2 * ky * kx;
      f.rWorld[2] = 2 * kz * kx;
      f.rWorld[3] = 2 * kx * ky;
      f.rWorld[4] = -1 + 2 * ky * ky;
      f.rWorld[5] = 2 * kz * ky;
      f.rWorld[6] = 2 * kx * kz;
      f.rWorld[7] = 2 * ky * kz;
      f.rWorld[8] = -1 + 2 * kz * kz;
      return;
    }
    const cax = uy * vz - uz * vy;
    const cay = uz * vx - ux * vz;
    const caz = ux * vy - uy * vx;
    const sinT = Math.hypot(cax, cay, caz);
    if (sinT < 1e-9) {
      f.rWorld.set([1, 0, 0, 0, 1, 0, 0, 0, 1]);
      return;
    }
    const kx = cax / sinT;
    const ky = cay / sinT;
    const kz = caz / sinT;
    const cosT = dot;
    const C = 1 - cosT;
    // Rodrigues R = I + sinT·[k]× + (1-cosT)·[k]×². Column-major.
    f.rWorld[0] = cosT + kx * kx * C;
    f.rWorld[1] = ky * kx * C + kz * sinT;
    f.rWorld[2] = kz * kx * C - ky * sinT;
    f.rWorld[3] = kx * ky * C - kz * sinT;
    f.rWorld[4] = cosT + ky * ky * C;
    f.rWorld[5] = kz * ky * C + kx * sinT;
    f.rWorld[6] = kx * kz * C + ky * sinT;
    f.rWorld[7] = ky * kz * C - kx * sinT;
    f.rWorld[8] = cosT + kz * kz * C;
  }
}

/**
 * Linear interpolation lookup of `halfBeams` at a body-X coordinate. Returns
 * 0 when `bodyX` is outside the sampled range so the caller can skip the
 * clamp gracefully (a fender swung past the bow/stern has no hull to hit).
 */
function sampleHalfBeam(prof: TopsidesProfile, bodyX: number): number {
  const n = prof.n;
  if (n < 2 || bodyX <= prof.xMin || bodyX >= prof.xMax) return 0;
  const t = ((bodyX - prof.xMin) / (prof.xMax - prof.xMin)) * (n - 1);
  const i0 = Math.floor(t);
  const i1 = Math.min(i0 + 1, n - 1);
  const a = t - i0;
  return prof.halfBeams[i0]! * (1 - a) + prof.halfBeams[i1]! * a;
}

/**
 * Sample a mesh-derived topsides half-beam profile from the player boat's
 * scene parts. For each body-X station the profile stores the maximum |Z|
 * of any vertex inside the Y band `[yMin, yMax]`, which should cover the
 * vertical range any deployed fender Attach can reach (a bit below the
 * waterline up to slightly above the rail). Empty stations are filled in
 * from the nearest sampled neighbour so the lookup never reads garbage.
 *
 * Vertex stream is the loader's 11-f32 PBR layout (pos at offset 0..2,
 * already in VF body frame after axis fix / yaw / roll / yOffset).
 */
export function buildTopsidesProfileFromScene(
  parts: ReadonlyArray<{
    verticesPbr: Float32Array;
    pivotPosition?: readonly [number, number, number];
  }>,
  opts: {
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
    numStations?: number;
  },
): TopsidesProfile {
  const n = Math.max(8, opts.numStations ?? 64);
  const halfBeams = new Float32Array(n);
  const xMin = opts.xMin;
  const xMax = opts.xMax;
  const yMin = opts.yMin;
  const yMax = opts.yMax;
  const range = xMax - xMin;
  if (range <= 1e-3) {
    return { xMin, xMax, n, halfBeams };
  }
  for (const part of parts) {
    const v = part.verticesPbr;
    const piv = part.pivotPosition ?? [0, 0, 0];
    const pX = piv[0]!;
    const pY = piv[1]!;
    const pZ = piv[2]!;
    for (let i = 0; i + 2 < v.length; i += 11) {
      const px = v[i]! + pX;
      const py = v[i + 1]! + pY;
      const pz = v[i + 2]! + pZ;
      if (py < yMin || py > yMax) continue;
      if (px < xMin || px > xMax) continue;
      const t = ((px - xMin) / range) * (n - 1);
      const idx = Math.round(t);
      const az = Math.abs(pz);
      if (az > halfBeams[idx]!) halfBeams[idx] = az;
    }
  }
  // Fill empty bins from nearest non-empty neighbour so the profile is
  // continuous along X (sparse meshes may leave gaps at small bin sizes).
  let lastNonZero = -1;
  for (let i = 0; i < n; i++) {
    if (halfBeams[i]! > 0) {
      if (lastNonZero >= 0 && lastNonZero < i - 1) {
        const v0 = halfBeams[lastNonZero]!;
        const v1 = halfBeams[i]!;
        for (let j = lastNonZero + 1; j < i; j++) {
          const a = (j - lastNonZero) / (i - lastNonZero);
          halfBeams[j] = v0 * (1 - a) + v1 * a;
        }
      } else if (lastNonZero < 0 && i > 0) {
        const v1 = halfBeams[i]!;
        for (let j = 0; j < i; j++) halfBeams[j] = v1;
      }
      lastNonZero = i;
    }
  }
  if (lastNonZero >= 0 && lastNonZero < n - 1) {
    const v0 = halfBeams[lastNonZero]!;
    for (let j = lastNonZero + 1; j < n; j++) halfBeams[j] = v0;
  }
  return { xMin, xMax, n, halfBeams };
}

/**
 * Build the 6 hanger specs from a boat's namedAnchors. The GLB authoring
 * convention is that hangers are authored on the port side only as
 * `Fender.Hanger.{Bow,Mid,Stern}`; the starboard mirror is generated here
 * by flipping the body-frame Z sign.
 *
 * Returns an empty array if the boat doesn't expose the required anchors;
 * callers should fall back to procedural hangers (e.g. {@link BoatHull.fenderHangers}).
 */
export function buildFenderHangersFromAnchors(
  anchors: Readonly<Record<string, Vec3>> | undefined,
): FenderHangerSpec[] {
  if (!anchors) return [];
  const pick = (...names: string[]): Vec3 | null => {
    for (const [k, v] of Object.entries(anchors)) {
      const norm = k.toLowerCase().replace(/[._\-\s]+/g, "");
      for (const want of names) {
        if (norm === want) return v;
      }
    }
    return null;
  };
  const bow = pick("fenderhangerbow");
  const mid = pick("fenderhangermid", "fenderhangermidship");
  const stern = pick("fenderhangerstern", "fenderhangeraft");
  if (!bow || !mid || !stern) return [];
  // Authored on one side only; force VF convention: port = -Z, starboard = +Z.
  const onPort = (p: Vec3): Vec3 => [p[0], p[1], -Math.abs(p[2])];
  const onStbd = (p: Vec3): Vec3 => [p[0], p[1], +Math.abs(p[2])];
  return [
    { key: "bow_port", bodyPos: onPort(bow) },
    { key: "bow_starboard", bodyPos: onStbd(bow) },
    { key: "midship_port", bodyPos: onPort(mid) },
    { key: "midship_starboard", bodyPos: onStbd(mid) },
    { key: "stern_port", bodyPos: onPort(stern) },
    { key: "stern_starboard", bodyPos: onStbd(stern) },
  ];
}
